/** "Refresh from <restech>…" (plan §3): a one-line confirmation on the Actions button —
 *  what a refresh does (one fetch by id, then the booking webhook's own processing,
 *  silently), the D8 warning when the booking is checked in locally, the lead for a party
 *  member — then the 202's background task, followed in place (`TaskProgressCard`).
 *
 *  Refusals read in the operator's terms: the kill switch, a booking its restech cannot be
 *  asked for (with "Reimport from stored XML…" instead, when permitted), and the
 *  per-booking cooldown with the seconds left. The page re-seeds when the task finishes
 *  (the host watches the task, so closing this popover early loses nothing). */
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, Button, LinearProgress, Popover, Typography } from '@mui/material';
import { useId, useState } from 'react';

import type { BookingRecordEditContext } from '@/api/bookingRecord';
import {
  BookingActionError,
  useRestechRefresh,
  type BookingActionWarning,
  type BookingRestechRefreshAccepted,
} from '@/api/bookingActions';
import { TaskProgressCard } from '@/features/bgtasks/TaskProgressCard';
import { Kbd } from '@/features/records/page/Kbd';

import { MONO_SX } from './panels/displays';

const PAPER_SX = { p: 1.5, mt: 0.5, width: 360, maxWidth: 'calc(100vw - 32px)', display: 'grid', gap: 1 } as const;

const CHECKED_IN_BY: Record<string, string> = {
  checkin_local: 'by the guide app',
  checkin_dashboard: 'on the dashboard',
  checkin_console: 'in the console',
};

/** The refresh's D8 warning before it is asked — the 202's `frozen` warning, up front. */
export function frozenWarning(ctx: BookingRecordEditContext, name: string): string | null {
  const reason = ctx.restech.suspended_reason;
  if (!reason) return null;
  return `The booking was checked in ${CHECKED_IN_BY[reason] ?? 'locally'}, so the refresh is skipped while it stays checked in: only a cancellation or a no-show from ${name} still applies.`;
}

function Warning({ children }: { children: string }) {
  return (
    <Typography
      variant="body2"
      component="p"
      sx={{ m: 0, display: 'flex', gap: 0.75, alignItems: 'flex-start', color: 'text.primary' }}
      data-refresh-warning
    >
      <WarningAmberIcon aria-hidden sx={{ fontSize: 16, mt: '2px', flex: 'none', color: 'warning.main' }} />
      <span>{children}</span>
    </Typography>
  );
}

function refusal(e: unknown, name: string): { text: string; reimport: boolean } {
  if (e instanceof BookingActionError) {
    const a = e.action;
    if (a.code === 'cooldown') {
      const s = a.retry_after_s;
      return {
        text: `${name} was asked for this booking moments ago — try again ${s ? `in ${s} s` : 'in a minute'}.`,
        reimport: false,
      };
    }
    if (a.code === 'disabled') return { text: 'Refreshing from the booking system is switched off on this environment.', reimport: false };
    return { text: a.detail, reimport: a.suggested_action === 'reimport_xml' };
  }
  return { text: e instanceof Error ? e.message : 'The refresh could not be requested.', reimport: false };
}

export function RestechRefreshPopover({
  bookingId,
  editContext,
  anchorEl,
  onClose,
  onQueued,
  onReimport,
}: {
  bookingId: number;
  editContext: BookingRecordEditContext;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** The 202 — the host watches its task. */
  onQueued: (accepted: BookingRestechRefreshAccepted) => void;
  /** Offered after a `refresh_unsupported` refusal; absent without the permission. */
  onReimport?: (() => void) | undefined;
}) {
  const titleId = useId();
  const name = editContext.restech.managed_by_display ?? editContext.restech.managed_by ?? 'the booking system';
  const refresh = useRestechRefresh(bookingId);
  const [accepted, setAccepted] = useState<BookingRestechRefreshAccepted | null>(null);
  const warning = frozenWarning(editContext, name);
  const lead = editContext.lead;
  const sending = refresh.isPending;

  const send = () =>
    refresh.mutate(undefined, {
      onSuccess: (r) => {
        setAccepted(r);
        onQueued(r);
      },
    });

  const failed = refresh.error ? refusal(refresh.error, name) : null;
  const warnings: BookingActionWarning[] = accepted?.warnings ?? [];

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={() => {
        if (!sending) onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          role: accepted ? 'dialog' : 'alertdialog',
          'aria-labelledby': titleId,
          'aria-busy': sending || undefined,
          'data-refresh': accepted ? 'queued' : failed ? 'failed' : 'confirm',
          sx: PAPER_SX,
        } as object,
      }}
    >
      <Typography id={titleId} variant="body2" sx={{ fontWeight: 600 }}>
        Refresh from {name}
      </Typography>

      {!accepted && !failed && (
        <>
          <Typography variant="body2" component="p" sx={{ m: 0 }}>
            Asks {name} for this booking now and imports what it sends, the way its webhook would — silently: no
            invitation, survey or update notification goes out.
          </Typography>
          {lead && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
              A party member: this refreshes its lead, booking #{lead.id} — the feed sends the lead only.
            </Typography>
          )}
          {warning && <Warning>{warning}</Warning>}
          <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
            A background task · one refresh a minute per booking · audited.
          </Typography>
          {sending && <LinearProgress aria-label="Requesting the refresh" sx={{ height: 2 }} />}
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} disabled={sending}>
              Back
            </Button>
            <Button size="small" variant="contained" autoFocus onClick={send} disabled={sending} data-confirm-refresh>
              {sending ? 'Requesting…' : 'Refresh now'}
              {!sending && <Kbd>↵</Kbd>}
            </Button>
          </Box>
        </>
      )}

      {failed && (
        <>
          <Alert severity="warning" sx={{ py: 0, '& .MuiAlert-message': { typography: 'body2', py: 0.75 } }}>
            Nothing was refreshed: {failed.text}
          </Alert>
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} autoFocus={!(failed.reimport && onReimport)}>
              Close
            </Button>
            {failed.reimport && onReimport && (
              <Button size="small" variant="contained" autoFocus onClick={onReimport}>
                Reimport from stored XML…
              </Button>
            )}
          </Box>
        </>
      )}

      {accepted && (
        <>
          <Typography variant="body2" component="p" sx={{ m: 0 }}>
            Queued: {accepted.integration_display} booking{' '}
            <Box component="span" sx={MONO_SX}>
              {accepted.vendor_booking_id}
            </Box>
            {accepted.booking_id !== bookingId ? ` (lead booking #${accepted.booking_id})` : ''}. The record reloads
            when it finishes.
          </Typography>
          {warnings.map((w) => (
            <Warning key={w.code}>{w.message}</Warning>
          ))}
          <TaskProgressCard taskId={accepted.task_id} dense />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} autoFocus>
              Close
            </Button>
          </Box>
        </>
      )}
    </Popover>
  );
}
